import { useLocale } from '../i18n/LocaleContext'
import { getRegistrationPath } from '../utils/routes'
import { useActivities } from '../hooks/useActivities'
import SectionHeading from '../components/ui/SectionHeading'
import Button from '../components/ui/Button'
import PlaceholderImage from '../components/ui/PlaceholderImage'
import SEOHead from '../components/SEOHead'

export default function Activities() {
  const { locale, t } = useLocale()
  const registrationPath = getRegistrationPath(locale)
  const { activities } = useActivities()

  // Prefer CMS-managed activities (with real images); fall back to the static
  // i18n list (no image yet → placeholder) until the CMS is populated.
  const items =
    activities.length > 0
      ? activities.map((a) => ({
          title: a.title[locale],
          description: a.description[locale],
          image: a.image,
        }))
      : t.activities.items.map((it) => ({
          title: it.title,
          description: it.description,
          image: '',
        }))

  return (
    <>
      <SEOHead title={t.activities.metaTitle} description={t.activities.metaDescription} />

      {/* Intro */}
      <section className="section-padding bg-surface">
        <div className="container-max">
          <SectionHeading title={t.activities.title} subtitle={t.activities.subtitle} />
          <p className="max-w-3xl mx-auto text-center text-text-secondary leading-relaxed">
            {t.activities.intro}
          </p>
        </div>
      </section>

      {/* Activities grid */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {items.map((item, i) => (
              <div
                key={i}
                className="flex flex-col overflow-hidden rounded-card border border-border transition-shadow hover:shadow-card"
              >
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.title}
                    width={800}
                    height={600}
                    className="w-full aspect-[4/3] object-cover"
                    loading="lazy"
                  />
                ) : (
                  <PlaceholderImage
                    label={item.title}
                    variant={i % 2 === 0 ? 'nature' : 'camp'}
                    className="w-full aspect-[4/3]"
                  />
                )}
                <div className="p-6">
                  <h3 className="font-heading font-semibold text-lg text-secondary mb-2">
                    {item.title}
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-secondary to-secondary-hover">
        <div className="container-max px-4 md:px-8 text-center">
          <h2 className="text-white mb-4">{t.activities.ctaTitle}</h2>
          <p className="text-gray-300 text-lg mb-8 max-w-xl mx-auto">{t.activities.ctaText}</p>
          <Button to={registrationPath} size="lg">
            {t.activities.cta}
          </Button>
        </div>
      </section>
    </>
  )
}
